import { del, get, post } from "@/infra/rest";
import requestConfig from "@/shared/config/request";

const jiraBaseURL = requestConfig.baseUrl;

// --- Jira Connect (integration management) ---

export interface JiraConnectResponse {
  authorize_url: string;
}

/** Returns URL for Atlassian OAuth. Frontend redirects user to this URL. */
export const getJiraConnectUrl = async (): Promise<JiraConnectResponse> => {
  return get<JiraConnectResponse>({
    url: "/v0/integrations/jira/connect",
    config: { baseURL: jiraBaseURL },
    isAuthRequired: true,
    useCookies: true,
  });
};

export interface JiraStatusResponse {
  connected: boolean;
  cloud_id?: string;
  site_name?: string;
  site_url?: string;
}

/** Returns current user's Jira connection status. */
export const getJiraStatus = async (): Promise<JiraStatusResponse> => {
  return get<JiraStatusResponse>({
    url: "/v0/integrations/jira/status",
    config: { baseURL: jiraBaseURL },
    isAuthRequired: true,
    useCookies: true,
  });
};

/** Disconnects current user's Jira. Success is 204. */
export const disconnectJira = async (): Promise<void> => {
  await del<void>({
    url: "/v0/integrations/jira/disconnect",
    config: { baseURL: jiraBaseURL },
    isAuthRequired: true,
    useCookies: true,
  });
};

// --- Create assessment from Jira (user session) ---

export interface CreateAssessmentFromJiraPayload {
  provider: "jira";
  ticket_id: string;
  /** Optional: use connected Jira when true */
  use_connected_jira?: boolean;
  doc_type?: string;
  idempotency_policy?: string;
}

export interface CreateAssessmentFromJiraResponse {
  /** Response shape depends on backend; minimal for now */
  [key: string]: unknown;
}

/** Creates an assessment from a Jira ticket. Uses current user's Jira OAuth when credentials not provided. */
export const createAssessmentFromJira = async (
  payload: CreateAssessmentFromJiraPayload
): Promise<CreateAssessmentFromJiraResponse> => {
  return post<CreateAssessmentFromJiraPayload, CreateAssessmentFromJiraResponse>({
    url: "/v0/integrations/tickets/create-assessment",
    data: payload,
    isAuthRequired: true,
    useCookies: true,
  });
};

// --- Get ticket (optional, for future use) ---

export interface GetTicketPayload {
  provider: "jira";
  ticket_id: string;
}

export const getJiraTicket = async (payload: GetTicketPayload): Promise<unknown> => {
  return post<GetTicketPayload, unknown>({
    url: "/v0/integrations/tickets",
    data: payload,
    isAuthRequired: true,
    useCookies: true,
  });
};

// --- Service token for Jira webhooks ---

export interface JiraWebhookTokenResponse {
  token: string;
}

/** Fetches service token for Jira webhooks. */
export const getJiraWebhookToken = async (): Promise<JiraWebhookTokenResponse> => {
  return get<JiraWebhookTokenResponse>({
    url: "/v0/integrations/jira/service-token",
    config: { baseURL: jiraBaseURL },
    isAuthRequired: true,
    useCookies: true,
  });
};
